import { useAiRecommendationCta } from '@cobliteam/ai';
import useAiRecommendationsStore, { type AiRecommendationJourney, permissionsConfig } from '@cobliteam/hooks/src/useAiRecommendationsStore/useAiRecommendationsStore';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import usePermissions from '@cobliteam/permissions/src/hooks/usePermissions.tsx';
import Tag from '@cobliteam/ui/components/Tag';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import AiSparkleIcon from '@cobliteam/ui-business/components/AiSparkleIcon';
import CriticalityTag from '../CriticalityTag';
import AiRecommendationBannerSkeleton from './components/AiRecommendationBannerSkeleton.tsx';

const BG_COLOR = '#F8F7FD';
const BORDER_GRADIENT = 'linear-gradient(90deg, #001BB8 0%, #9847EE 100%)';

interface AiRecommendationBannerProps {
  journeyId: AiRecommendationJourney;
  /** Custom action when AI CTA is clicked (aiChat enabled). Overrides default navigation to /ai. */
  action?: () => void;
  fallbackLabel?: string;
  fallbackAction?: () => void;
}

const AiRecommendationBanner = ({ journeyId, action, fallbackLabel, fallbackAction }: AiRecommendationBannerProps) => {
  const { aiRecommendations } = useFeatureFlags();
  const { recommendations, improvementRecommendations, progress, isLoading, isError } = useAiRecommendationsStore();
  const { isAccessAllowed } = usePermissions();

  const { ctaButton } = useAiRecommendationCta({
    component: 'ai-recommendation-banner',
    journeyId: journeyId,
    action: action,
    fallbackLabel: fallbackLabel,
    fallbackAction: fallbackAction,
  });

  if (!aiRecommendations) return null;
  const allRecommendations = [...recommendations, ...improvementRecommendations];
  const recommendation = allRecommendations.find((r) => r.journeyId === journeyId) ?? null;

  if (isLoading) return <AiRecommendationBannerSkeleton />;
  if (!recommendation || isError) return null;

  const permission = permissionsConfig[recommendation.journeyId];

  if (!isAccessAllowed(permission)) {
    return null;
  }

  const allCompleted = progress.totalJourneys > 0 && progress.completedJourneys >= progress.totalJourneys;

  return (
    <div
      className='flex w-full items-center gap-4 rounded-2xl px-5 py-4'
      style={{
        background: BG_COLOR,
        border: '1px solid transparent',
        backgroundImage: `linear-gradient(${BG_COLOR}, ${BG_COLOR}), ${BORDER_GRADIENT}`,
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
      }}
    >
      {/* Gradient-bordered icon */}
      <div
        className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl'
        style={{
          border: '1.5px solid transparent',
          backgroundImage: `linear-gradient(${BG_COLOR}, ${BG_COLOR}), ${BORDER_GRADIENT}`,
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
        }}
      >
        <AiSparkleIcon size={22} />
      </div>

      {/* Content */}
      <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <div className='flex items-center gap-3'>
          <Typography variant='heading-md'>{recommendation.journeyName}</Typography>
          {recommendation.criticality && <CriticalityTag criticality={recommendation.criticality} />}
          {recommendation.estimatedMinutes > 0 && (
            <Tag variant='outlined' color='default' label={`${recommendation.estimatedMinutes} min`} icon={<Icon name='clock' size='xs' />} />
          )}
        </div>
        <Typography variant='description-list-default' color='secondary'>
          {recommendation.shortDescription}
        </Typography>
      </div>

      {/* CTA + Dismiss */}
      <div className='flex shrink-0 items-center gap-3'>{!allCompleted && ctaButton}</div>
    </div>
  );
};

export default AiRecommendationBanner;
